import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse, HttpContextToken } from '@angular/common/http';

export const NO_LOADER = new HttpContextToken<boolean>(() => false);
import { from, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap, share } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/pages/auth/auth.service';
import { LayoutService } from '../services/layout.service';
import { LocalStorageService } from '../services/local-storage.service';

@Injectable()
export class Interceptor implements HttpInterceptor {
  constructor(
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly loaderService: LayoutService,
    private readonly service: AuthService,
    private readonly localStorageService: LocalStorageService
  ) {}

  isLoginPage(): boolean {
    return this.router.url === '/auth';
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const noLoader = request.context.get(NO_LOADER);
    const token = this.localStorageService.getItem('token') ?? '';
    if (token) {
      request = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${token}`),
      });
    }

    request = request.clone({
      headers: request.headers.set('Accept', 'application/json; charset=utf-8'),
    });

    if (!noLoader) {
      this.loaderService.show();
    }

    return this.makeRequest(request, next);
  }

  makeRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const req: Observable<HttpEvent<any>> = next.handle(request).pipe(
      share(),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (request.method !== 'GET') {
            if (event.body && event.body.success) {
              this.toastrService.success(event.body.message, '', {});
            } else if (event.body && !event.body.success) {
              this.toastrService.error(event.body.message, 'Attenzione!', {});
            }
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        return from(this.parseError(err)).pipe(
          tap((parsedError) => {
            const messageError = parsedError?.message || err.error?.message || err?.message || 'Si è verificato un problema';

            if (err.status === 401) {
              this.refreshToken(err);
            } else if (err.status === 422 || err.status === 400) {
              const errorMessages = Array.isArray(parsedError?.errors)
                ? '<ul>' + parsedError.errors.map((e: string) => `<li>${e}</li>`).join('') + '</ul>'
                : '';

              this.toastrService.error(messageError + errorMessages, 'Attenzione', {
                enableHtml: true,
              });
            } else {
              if (err.status == null || err.status === 0 || !navigator.onLine) {
                this.toastrService.error('Connessione internet assente', 'Attenzione', { enableHtml: true });
              } else {
                this.toastrService.error(messageError, 'Si è verificato un problema', {});
              }
            }
          }),
          switchMap(() => throwError(() => err))
        );
      }),
      finalize(() => {
        this.loaderService.hide();
      })
    );

    return req;
  }

  refreshToken(err: any) {
    const token = this.localStorageService.getItem('token');
    const rememberMe = this.localStorageService.getItem('rememberMe');
    if (token && rememberMe === 'true') {
      this.service.refreshToken(token).subscribe({
        next: (res: any) => {
          this.localStorageService.setItem([{ key: 'token', value: res.data.token }]);
          window.location.reload();
        },
        error: (err) => {
          this.sessionExpired(err);
        },
      });
    } else {
      this.sessionExpired(err);
    }
  }

  private parseError(err: HttpErrorResponse): Promise<any> {
    return new Promise((resolve) => {
      if (err.error instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            resolve(JSON.parse(reader.result as string));
          } catch {
            resolve(null);
          }
        };
        reader.readAsText(err.error);
      } else {
        resolve(err.error);
      }
    });
  }

  sessionExpired(err: any) {
    this.localStorageService.clear();
    if (err instanceof HttpErrorResponse) {
      try {
        if (!this.isLoginPage()) {
          this.toastrService.error('Sessione scaduta', 'Attenzione!', {});
          setTimeout(() => (window.location.href = window.location.origin + '/auth'), 3000);
        }
      } catch (e) {
        this.toastrService.error('Errore', '', {});
        setTimeout(() => (window.location.href = window.location.origin + '/auth'), 3000);
      }
    }
  }
}
